import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { usePlans, useCreateDeposit, useDeposits } from "@/lib/data-hooks";
import { fmtMoney, fmtDate } from "@/lib/format";
import { Badge } from "@/components/ui/badge";
import { Copy } from "lucide-react";
import { toast } from "sonner";

export const Route = createFileRoute("/_authenticated/deposit")({
  head: () => ({ meta: [{ title: "Deposit — CryptoBull Pro" }] }),
  validateSearch: (s: Record<string, unknown>) => ({ plan: typeof s.plan === "string" ? s.plan : undefined }),
  component: Deposit,
});

const WALLETS = {
  BTC: "bc1q8kylrltsgmfznwk53fxcjce899uzn6gkt8ve4f",
} as const;

function Deposit() {
  const { plan: planSlug } = Route.useSearch();
  const { data: plans = [] } = usePlans();
  const { data: deposits = [] } = useDeposits();
  const [planId, setPlanId] = useState<string | undefined>(plans.find((p) => p.slug === planSlug)?.id);
  const [amount, setAmount] = useState<string>("");
  const asset = "BTC" as const;
  const create = useCreateDeposit();
  const address = WALLETS[asset];

  return (
    <div className="space-y-6 max-w-4xl">
      <div>
        <h1 className="text-2xl font-semibold">Deposit funds</h1>
        <p className="text-sm text-muted-foreground">Choose a network, send the funds to the address below, then submit the request for admin review.</p>
      </div>

      <div className="glass rounded-2xl p-6">
        <div className="text-xs uppercase tracking-widest text-muted-foreground">Bitcoin (BTC) funding address</div>
        <div className="mt-2 flex items-center gap-2">
          <code className="font-mono text-sm break-all">{address}</code>
          <Button size="icon" variant="ghost" onClick={() => { navigator.clipboard.writeText(address); toast.success("Address copied"); }}><Copy className="h-4 w-4" /></Button>
        </div>
        <p className="mt-2 text-xs text-muted-foreground">Only send BTC on the Bitcoin network. Other networks may result in lost funds.</p>
      </div>

      <form className="glass rounded-2xl p-6 space-y-4" onSubmit={(e) => {
        e.preventDefault();
        const amt = Number(amount);
        if (!amt || amt < 1) { toast.error("Enter a valid amount"); return; }
        create.mutate({ amount: amt, plan_id: planId ?? null, btc_address: address, asset });
        setAmount("");
      }}>
        <div className="grid sm:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Plan</Label>
            <Select value={planId} onValueChange={setPlanId}>
              <SelectTrigger><SelectValue placeholder="Select plan (optional)" /></SelectTrigger>
              <SelectContent>
                {plans.map((p) => (
                  <SelectItem key={p.id} value={p.id}>{p.name} — {Number(p.daily_roi)}%/day · {p.duration_days}d</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Amount (USD)</Label>
            <Input type="number" min="1" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="500.00" />
          </div>
        </div>
        <Button type="submit" disabled={create.isPending}>{create.isPending ? "Submitting…" : "Submit deposit request"}</Button>
      </form>

      <div className="glass rounded-2xl p-6">
        <div className="text-sm font-medium mb-4">Recent deposits</div>
        {deposits.length === 0 ? <div className="text-sm text-muted-foreground py-6 text-center">No deposits yet</div> : (
          <div className="space-y-2">
            {deposits.map((d) => (
              <div key={d.id} className="flex items-center justify-between rounded-lg px-3 py-2 hover:bg-white/5">
                <div>
                  <div className="text-sm">{fmtMoney(d.amount)} {d.plan ? <span className="text-muted-foreground">· {d.plan.name}</span> : null}</div>
                  <div className="text-[11px] text-muted-foreground">{fmtDate(d.created_at)}</div>
                </div>
                <Badge variant={d.status === "approved" ? "default" : "outline"} className="capitalize">{d.status}</Badge>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}